import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';
import type { MarketplaceListing, MarketplaceFilters, MarketplaceNegotiation, MarketplaceMessage } from '@/types/marketplace';
import type { UserReputation } from '@/types/reputation';
import { createMarketplaceNotification } from '@/services/notifications';
import { buildPublicProfileMap } from '@/services/profileUtils';

type MarketplaceListingInsert = Database['public']['Tables']['marketplace_listings']['Insert'];
type MarketplaceListingUpdate = Database['public']['Tables']['marketplace_listings']['Update'];

// ─── Shared seller enrichment utility ───
interface SellerEnrichment {
  profiles: Map<string, { name: string | null; avatar: string | null; is_verified: boolean }>;
  reputations: Map<string, UserReputation>;
  activity: Map<string, string | null>;
}

async function enrichWithSellerData(sellerIds: string[]): Promise<SellerEnrichment> {
  const [profileMap, reputationResult] = await Promise.all([
    buildPublicProfileMap(sellerIds),
    supabase.from('user_reputation').select('*').in('user_id', sellerIds),
  ]);

  const profiles = new Map(
    Object.entries(profileMap).map(([userId, p]) => [userId, { name: p.display_name, avatar: p.avatar_url, is_verified: false }])
  );

  const reputations = new Map(
    (reputationResult.data || []).map((r) => {
      return [r.user_id, { ...r, boost_plan_total: 0, boost_performance_total: 0 } as UserReputation];
    })
  );

  const activity = new Map<string, string | null>();

  return { profiles, reputations, activity };
}

// Only select fields needed for ListingCard - improves query performance
const LISTING_CARD_FIELDS = `
  id,
  seller_id,
  title,
  short_description,
  listing_type,
  service_type,
  price,
  original_price,
  followers_count,
  is_monetized,
  tiktok_shop_enabled,
  has_restrictions,
  account_country,
  seller_role,
  thumbnail_url,
  thumbnail_position_y,
  created_at,
  priority_score,
  is_persistent,
  completed_sales_count
`;

export async function fetchListings(filters?: MarketplaceFilters): Promise<MarketplaceListing[]> {
  let query = supabase
    .from('marketplace_listings')
    .select(LISTING_CARD_FIELDS)
    .eq('status', 'active');

  if (filters?.listing_type) {
    query = query.eq('listing_type', filters.listing_type);
  }
  if (filters?.service_type) {
    query = query.eq('service_type', filters.service_type);
  }
  if (filters?.account_country) {
    query = query.eq('account_country', filters.account_country);
  }
  if (filters?.min_price !== undefined) {
    query = query.gte('price', filters.min_price);
  }
  if (filters?.max_price !== undefined) {
    query = query.lte('price', filters.max_price);
  }
  if (filters?.min_followers !== undefined) {
    query = query.gte('followers_count', filters.min_followers);
  }
  if (filters?.max_followers !== undefined) {
    query = query.lte('followers_count', filters.max_followers);
  }
  if (filters?.is_monetized !== undefined) {
    query = query.eq('is_monetized', filters.is_monetized);
  }
  if (filters?.tiktok_shop_enabled !== undefined) {
    query = query.eq('tiktok_shop_enabled', filters.tiktok_shop_enabled);
  }
  if (filters?.has_restrictions !== undefined) {
    query = query.eq('has_restrictions', filters.has_restrictions);
  }

  // Sorting - default to priority_score DESC for ranking
  switch (filters?.sort_by) {
    case 'price_asc':
      query = query.order('price', { ascending: true });
      break;
    case 'price_desc':
      query = query.order('price', { ascending: false });
      break;
    case 'rating':
      query = query.order('priority_score', { ascending: false }).order('created_at', { ascending: true });
      break;
    default:
      // Default: priority_score DESC (Master first), then created_at ASC (oldest first as tiebreaker)
      query = query.order('priority_score', { ascending: false }).order('created_at', { ascending: true });
  }

  const { data, error } = await query;
  if (error) {
    console.warn('fetchListings: query failed, returning empty array:', error.message);
    return [];
  }
  
  const listings = data || [];
  
  // Enrich with seller data
  if (listings.length > 0) {
    const sellerIds = [...new Set(listings.map((listing) => listing.seller_id))];
    try {
      const enrichment = await enrichWithSellerData(sellerIds);
      return listings.map((item) => ({
        ...item,
        seller_name: enrichment.profiles.get(item.seller_id)?.name || null,
        seller_avatar_url: enrichment.profiles.get(item.seller_id)?.avatar || null,
        seller_is_verified: enrichment.profiles.get(item.seller_id)?.is_verified || false,
        seller_reputation: enrichment.reputations.get(item.seller_id) || null,
        seller_last_seen_at: enrichment.activity.get(item.seller_id) || null,
      })) as (MarketplaceListing & { seller_reputation?: UserReputation })[];
    } catch (err) {
      console.warn('Failed to fetch seller profiles/reputation, continuing without:', err);
    }
  }
  
  return listings as MarketplaceListing[];
}

// Full listing details columns (for detail page)
const LISTING_DETAIL_FIELDS = `
  id, seller_id, title, short_description, listing_type, service_type,
  price, original_price, followers_count, engagement_28d, is_monetized,
  tiktok_shop_enabled, tiktok_shop_documented, has_restrictions, account_country,
  seller_role, thumbnail_url, thumbnail_position_y, views_count,
  status, expires_at, created_at, updated_at, priority_score,
  is_persistent, completed_sales_count, warranty_days, warranty_description
`;

export async function fetchListingById(id: string): Promise<MarketplaceListing | null> {
  const { data, error } = await supabase
    .from('marketplace_listings')
    .select(LISTING_DETAIL_FIELDS)
    .eq('id', id)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  // Enrich with seller data using shared utility
  try {
    const enrichment = await enrichWithSellerData([data.seller_id]);
    const profile = enrichment.profiles.get(data.seller_id);
    return {
      ...data,
      seller_name: profile?.name || null,
      seller_avatar_url: profile?.avatar || null,
      seller_is_verified: profile?.is_verified || false,
      seller_reputation: enrichment.reputations.get(data.seller_id) || null,
      seller_last_seen_at: enrichment.activity.get(data.seller_id) || null,
    } as MarketplaceListing;
  } catch (err) {
    console.warn('Failed to fetch seller profile/reputation, continuing without:', err);
    return data as MarketplaceListing;
  }
}

export async function createListing(
  listing: Partial<
    Omit<
      MarketplaceListing,
      'id' | 'created_at' | 'updated_at' | 'expires_at' | 'views_count' | 'status'
    >
  > & {
    seller_id: string;
    listing_type: MarketplaceListing['listing_type'];
    title: string;
    price: number;
  }
): Promise<MarketplaceListing> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) {
    throw new Error('Sessão expirada. Faça login novamente.');
  }

  // Always trust the authenticated user id (prevents RLS mismatch)
  const {
    seller_name: _sellerName,
    seller_avatar_url: _sellerAvatarUrl,
    seller_is_verified: _sellerIsVerified,
    seller_reputation: _sellerReputation,
    seller_last_seen_at: _sellerLastSeenAt,
    ...listingFields
  } = listing;
  const payload: MarketplaceListingInsert = {
    ...listingFields,
    seller_id: session.user.id,
  };

  const { data, error } = await supabase
    .from('marketplace_listings')
    .insert(payload)
    .select()
    .single();

  if (error) throw error;

  // Fire-and-forget activity log
  const { logActivityWithProfile } = await import('@/services/activityLog');
  logActivityWithProfile('listing_created', session.user.id, listing.title, data.id);

  return data as MarketplaceListing;
}

export async function updateListing(id: string, updates: MarketplaceListingUpdate): Promise<MarketplaceListing> {
  const { data, error } = await supabase
    .from('marketplace_listings')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as MarketplaceListing;
}

export async function deleteListing(id: string): Promise<void> {
  const { error } = await supabase
    .from('marketplace_listings')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Count active negotiations for a specific listing
export async function fetchListingActiveNegotiationsCount(listingId: string): Promise<number> {
  const { count, error } = await supabase
    .from('marketplace_negotiations')
    .select('*', { count: 'exact', head: true })
    .eq('listing_id', listingId)
    .eq('status', 'active');

  if (error) throw error;
  return count || 0;
}

// Delete listing with cascade via server-side RPC (handles cancellation stats)
export async function deleteListingWithCascade(listingId: string): Promise<{ cancelledCount: number }> {
  const { data, error } = await supabase.rpc('delete_listing_cascade', { p_listing_id: listingId });

  if (error) throw error;

  const result = data as { success: boolean; error?: string; cancelled_count?: number };
  if (!result.success) throw new Error(result.error || 'Erro ao excluir anúncio');

  return { cancelledCount: result.cancelled_count || 0 };
}

export async function canEditListing(listingId: string): Promise<{ can_edit: boolean; reason?: string; current_price?: number; original_price?: number | null }> {
  const { data, error } = await supabase.rpc('can_edit_listing', { _listing_id: listingId });
  
  if (error) throw error;
  return data as { can_edit: boolean; reason?: string; current_price?: number; original_price?: number | null };
}

export async function updateListingPrice(listingId: string, newPrice: number, originalPrice: number | null): Promise<void> {
  // First verify permission
  const canEdit = await canEditListing(listingId);
  
  if (!canEdit.can_edit) {
    throw new Error(canEdit.reason || 'Não é possível editar este anúncio');
  }
  
  // Validate new price is positive and different from current
  const currentPrice = canEdit.current_price || 0;
  if (newPrice <= 0) {
    throw new Error('Digite um valor válido');
  }
  if (newPrice === currentPrice) {
    throw new Error('O novo preço é igual ao atual');
  }

  // If new price is greater than or equal to the original (or there's no original),
  // clear original_price — there's no longer a discount to display as strikethrough.
  // Otherwise, preserve the original for the discount strikethrough.
  const effectiveOriginal = originalPrice ?? currentPrice;
  const nextOriginalPrice = newPrice < effectiveOriginal ? effectiveOriginal : null;

  const { error } = await supabase
    .from('marketplace_listings')
    .update({ 
      price: newPrice,
      original_price: nextOriginalPrice,
    })
    .eq('id', listingId);
    
  if (error) throw error;
}

export async function updateListingDetails(
  listingId: string,
  fields: {
    title?: string;
    short_description?: string | null;
    thumbnail_url?: string;
    thumbnail_position_y?: number;
    followers_count?: number | null;
    warranty_days?: number | null;
    warranty_description?: string | null;
  }
): Promise<void> {
  const { error } = await supabase
    .from('marketplace_listings')
    .update(fields)
    .eq('id', listingId);

  if (error) throw error;
}

// Optimized negotiation columns
const NEGOTIATION_LIST_COLUMNS = `
  id, listing_id, buyer_id, seller_id, status, created_at, last_activity_at,
  buyer_accepted_terms, seller_accepted_terms, buyer_confirmed_complete,
  seller_confirmed_complete, completed_at, cancelled_at, cancelled_by, expired_at
`;

// Result type for get_or_create_negotiation RPC
interface GetOrCreateNegotiationResult {
  success: boolean;
  negotiation_id?: string;
  is_existing?: boolean;
  error?: string;
  active_negotiation_id?: string;
}

export async function createNegotiation(
  listing_id: string,
  seller_id: string
): Promise<{ negotiation: MarketplaceNegotiation; isExisting: boolean }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('User not authenticated');
  const user = session.user;

  // Use atomic RPC to prevent duplicates
  const { data: rpcResult, error: rpcError } = await supabase.rpc('get_or_create_negotiation', {
    p_listing_id: listing_id,
    p_seller_id: seller_id
  });

  if (rpcError) throw rpcError;
  
  const result = rpcResult as unknown as GetOrCreateNegotiationResult;
  
  if (!result.success) {
    throw new Error(result.error || 'Erro ao criar negociação');
  }

  // Fetch the negotiation data
  const { data: negotiation, error: fetchError } = await supabase
    .from('marketplace_negotiations')
    .select('*')
    .eq('id', result.negotiation_id)
    .single();

  if (fetchError) throw fetchError;

  // Only for NEW negotiations: mark buyer_accepted_terms = true and notify seller
  if (!result.is_existing) {
    // Mark buyer's terms as accepted (they accepted in ListingDetails before creating)
    const { error: updateError } = await supabase
      .from('marketplace_negotiations')
      .update({ 
        buyer_accepted_terms: true, 
        buyer_terms_accepted_at: new Date().toISOString() 
      })
      .eq('id', result.negotiation_id);
    
    if (updateError) {
      console.error('Erro ao marcar termos do comprador:', updateError);
    }

    const [listingResult, buyerResult] = await Promise.all([
      supabase.from('marketplace_listings').select('title').eq('id', listing_id).single(),
      (async () => ({ data: (await buildPublicProfileMap([user.id]))[user.id] || null }))()
    ]);

    const buyerData = buyerResult.data as unknown as { display_name: string | null } | null;
    
    // Use the RPC function to create notification (bypasses RLS)
    try {
      await createMarketplaceNotification(seller_id, 'new_negotiation', {
        negotiationId: result.negotiation_id!,
        listingTitle: listingResult.data?.title || 'Anúncio',
        otherUserName: buyerData?.display_name || 'Comprador'
      });
    } catch (notifError) {
      console.error('Erro ao criar notificação para vendedor:', notifError);
      // Don't throw - notification is non-critical
    }
  }

  // Fire-and-forget activity log for new negotiations
  if (!result.is_existing) {
    import('@/services/activityLog').then(({ logActivityWithProfile }) => {
      logActivityWithProfile('negotiation_started', user.id, undefined, result.negotiation_id || undefined);
    });
  }

  return { 
    negotiation: negotiation as MarketplaceNegotiation, 
    isExisting: result.is_existing || false 
  };
}

export async function updateNegotiationTerms(
  id: string,
  role: 'buyer' | 'seller'
): Promise<void> {
  const updates = role === 'buyer'
    ? { buyer_accepted_terms: true, buyer_terms_accepted_at: new Date().toISOString() }
    : { seller_accepted_terms: true, seller_terms_accepted_at: new Date().toISOString() };

  const { error } = await supabase
    .from('marketplace_negotiations')
    .update(updates)
    .eq('id', id);

  if (error) throw error;
}

// Optimized message columns
const MESSAGE_COLUMNS = `id, negotiation_id, sender_id, content, is_read, created_at, file_url, file_name, file_type, is_admin_message`;

// Messages
export async function fetchMessages(negotiation_id: string): Promise<MarketplaceMessage[]> {
  const { data, error } = await supabase
    .from('marketplace_messages')
    .select(MESSAGE_COLUMNS)
    .eq('negotiation_id', negotiation_id)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return (data || []) as MarketplaceMessage[];
}

export async function sendMessage(
  negotiation_id: string, 
  content: string,
  fileData?: { file_url: string; file_name: string; file_type: string }
): Promise<MarketplaceMessage> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('User not authenticated');
  const user = session.user;

  const insertPayload = {
    negotiation_id,
    sender_id: user.id,
    content,
    ...(fileData ? {
      file_url: fileData.file_url,
      file_name: fileData.file_name,
      file_type: fileData.file_type,
    } : {}),
  };

  const { data, error } = await supabase
    .from('marketplace_messages')
    .insert([insertPayload])
    .select()
    .single();

  if (error) throw error;

  // Update last activity and sender's presence atomically
  await Promise.all([
    supabase
      .from('marketplace_negotiations')
      .update({ last_activity_at: new Date().toISOString() })
      .eq('id', negotiation_id),
    supabase.rpc('update_negotiation_presence', { p_negotiation_id: negotiation_id })
  ]);

  // Message notification is now handled by database trigger (trg_notify_marketplace_message)

  return data as MarketplaceMessage;
}

// ============================================================
// File Upload for Negotiation Chat
// ============================================================

const ALLOWED_IMAGE_TYPES = [
  'image/jpeg', 'image/png', 'image/gif', 'image/webp',
  'image/heic', 'image/heif',
];
const ALLOWED_FILE_TYPES = [...ALLOWED_IMAGE_TYPES, 'application/pdf'];

/** Images may be picked up to 10MB — they are compressed before upload. */
const MAX_IMAGE_INPUT_BYTES = 10 * 1024 * 1024;
/** Final size that actually goes to storage (also the hard limit for PDFs). */
const MAX_UPLOAD_BYTES = 1 * 1024 * 1024;

function isImageType(type: string) {
  return type.startsWith('image/') || ALLOWED_IMAGE_TYPES.includes(type);
}

/**
 * Check if the user can still upload files in this negotiation (5 files per participant per negotiation)
 */
export async function checkFileQuota(negotiationId: string, userId: string): Promise<boolean> {
  const { count, error } = await supabase
    .from('marketplace_messages')
    .select('id', { count: 'exact', head: true })
    .eq('negotiation_id', negotiationId)
    .eq('sender_id', userId)
    .not('file_url', 'is', null);

  if (error) throw error;
  return (count || 0) < 5; // true = can still upload
}

/**
 * Upload a file attached to a negotiation chat message
 * Images are compressed to WebP; PDFs are uploaded as-is.
 * Returns the public URL and file metadata.
 */
export async function uploadNegotiationFile(
  negotiationId: string,
  file: File,
  role: 'buyer' | 'seller'
): Promise<{ file_url: string; file_name: string; file_type: string }> {
  const isImage = isImageType(file.type);

  // Validate file type
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    throw new Error('Tipo de arquivo não permitido. Envie uma imagem (JPG, PNG, GIF, WebP) ou um PDF.');
  }

  // Size validation: images are compressed first, so only the input ceiling
  // applies here. PDFs go as-is and must already fit the final limit.
  if (isImage && file.size > MAX_IMAGE_INPUT_BYTES) {
    throw new Error('Imagem muito grande. O tamanho máximo é 10MB.');
  }
  if (!isImage && file.size > MAX_UPLOAD_BYTES) {
    throw new Error('PDF muito grande. O tamanho máximo é 1MB.');
  }

  let uploadBlob: Blob = file;
  let extension = file.name.split('.').pop() || 'bin';
  let mimeType = file.type;

  // Compress images
  if (isImage) {
    const { prepareImageForUpload } = await import('@/lib/imageUtils');
    try {
      const result = await prepareImageForUpload(file, 'chatAttachment');
      uploadBlob = result.blob;
      extension = result.extension;
      mimeType = result.mimeType;
    } catch {
      throw new Error('Não foi possível processar essa imagem. Tire um print da tela ou salve o arquivo como JPG e tente novamente.');
    }

    if (uploadBlob.size > MAX_UPLOAD_BYTES) {
      throw new Error('Imagem muito pesada mesmo após a compressão. Tente enviar uma imagem menor.');
    }
  }

  const timestamp = Date.now();
  const filePath = `${negotiationId}/${role}/${timestamp}.${extension}`;

  const { error: uploadError } = await supabase.storage
    .from('negotiation-files')
    .upload(filePath, uploadBlob, {
      contentType: mimeType,
      cacheControl: '3600',
    });

  if (uploadError) {
    const raw = uploadError.message || '';
    if (/row-level security|policy|unauthorized|403/i.test(raw)) {
      throw new Error('Não é possível anexar arquivos nesta negociação no momento.');
    }
    if (/exceeded the maximum allowed size|payload too large|413/i.test(raw)) {
      throw new Error('Arquivo muito grande. Tente enviar uma imagem menor.');
    }
    console.error('[uploadNegotiationFile]', raw);
    throw new Error('Não foi possível enviar o arquivo. Tente novamente em instantes.');
  }

  // Store the file path (not public URL) since the bucket is private
  // The frontend will generate signed URLs for viewing
  return {
    file_url: filePath,
    file_name: file.name,
    file_type: mimeType,
  };
}


// Fetch both negotiation counts in a single call (1 session, 2 parallel queries)
export async function fetchNegotiationsCounts(): Promise<{ active: number; total: number }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return { active: 0, total: 0 };
  
  const userId = session.user.id;
  
  const [activeResult, totalResult] = await Promise.all([
    supabase
      .from('marketplace_negotiations')
      .select('*', { count: 'exact', head: true })
      .eq('status', 'active')
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`),
    supabase
      .from('marketplace_negotiations')
      .select('*', { count: 'exact', head: true })
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`),
  ]);
  
  return {
    active: activeResult.count || 0,
    total: totalResult.count || 0,
  };
}

// Fetch all negotiations for current user (filters out individually deleted chats)
export async function fetchUserNegotiations(): Promise<(MarketplaceNegotiation & { listing?: MarketplaceListing })[]> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return [];
  
  const userId = session.user.id;
  
  const [negsResult, deletionsResult] = await Promise.all([
    supabase
      .from('marketplace_negotiations')
      .select('*')
      .or(`buyer_id.eq.${userId},seller_id.eq.${userId}`)
      .order('last_activity_at', { ascending: false }),
    supabase
      .from('negotiation_chat_deletions')
      .select('negotiation_id')
      .eq('user_id', userId),
  ]);
  
  if (negsResult.error) throw negsResult.error;
  
  const deletedIds = new Set((deletionsResult.data || []).map(d => d.negotiation_id));
  const negotiations = (negsResult.data || []).filter(n => !deletedIds.has(n.id));
  
  const listingIds = [...new Set(negotiations.map(n => n.listing_id))];
  
  if (listingIds.length === 0) return negotiations as (MarketplaceNegotiation & { listing?: MarketplaceListing })[];
  
  const { data: listingsData } = await supabase
    .from('marketplace_listings')
    .select('*')
    .in('id', listingIds);
  
  const listingsMap = new Map((listingsData || []).map(l => [l.id, l]));
  
  return negotiations.map(n => ({
    ...n,
    listing: listingsMap.get(n.listing_id) as MarketplaceListing | undefined,
  })) as (MarketplaceNegotiation & { listing?: MarketplaceListing })[];
}

// Delete (hide) a negotiation chat for the current user only
export async function deleteNegotiationChat(negotiationId: string): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('Not authenticated');
  
  const { error } = await supabase
    .from('negotiation_chat_deletions')
    .insert({ user_id: session.user.id, negotiation_id: negotiationId });
  
  if (error) throw error;
}

// Upload thumbnail (with compression)
export async function uploadThumbnail(file: File): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) throw new Error('User not authenticated');
  const user = session.user;

  // Import and compress image
  const { prepareImageForUpload } = await import('@/lib/imageUtils');
  const { blob, extension, mimeType } = await prepareImageForUpload(file, 'marketplaceThumbnail');
  
  const fileName = `${user.id}/${Date.now()}.${extension}`;

  const { error } = await supabase.storage
    .from('marketplace-thumbnails')
    .upload(fileName, blob, {
      contentType: mimeType
    });

  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('marketplace-thumbnails')
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Fetch negotiations count separated by role (buyer vs seller)
 */
export async function fetchNegotiationsCountByRole(): Promise<{
  as_buyer: number;
  as_seller: number;
  active_as_buyer: number;
  active_as_seller: number;
  cancelled_as_seller: number;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return { as_buyer: 0, as_seller: 0, active_as_buyer: 0, active_as_seller: 0, cancelled_as_seller: 0 };

  const { data, error } = await supabase.rpc('get_negotiation_counts_by_role', {
    p_user_id: session.user.id,
  });

  if (error || !data) {
    console.error('Error fetching negotiation counts:', error);
    return { as_buyer: 0, as_seller: 0, active_as_buyer: 0, active_as_seller: 0, cancelled_as_seller: 0 };
  }

  const counts = typeof data === 'object' && data !== null && !Array.isArray(data)
    ? data as Record<string, unknown>
    : {};
  const count = (key: string) => typeof counts[key] === 'number' ? counts[key] : 0;
  return {
    as_buyer: count('as_buyer'),
    as_seller: count('as_seller'),
    active_as_buyer: count('active_as_buyer'),
    active_as_seller: count('active_as_seller'),
    cancelled_as_seller: count('cancelled_as_seller'),
  };
}

// Record a unique listing view (fire-and-forget, no error thrown)
export async function recordListingView(listingId: string): Promise<void> {
  try {
    await supabase.rpc('record_listing_view', { p_listing_id: listingId });
  } catch (err) {
    console.error('[marketplace] Error recording listing view:', err);
  }
}
